import { FC, useContext, useEffect, useRef, useState } from "react"

import { Flex, Space, Typography } from "antd"

import { ColorifiedButton } from "@/components/_Shared/Button/ColorifiedButton"
import { Table } from "@/components/_Shared/Table"
import { BulkOrderAddForm, BulkOrderAddFormProps } from "@/components/BulkOrder/BulkOrderAddForm/BulkOrderAddForm"
import BulkOrderTimer from "@/components/BulkOrder/BulkOrderTimer/BulkOrderTimer"
import RequestQuoteInfo from "@/components/BulkOrder/RequestQuoteInfo/RequestQuoteInfo"
import { Resource, Scope } from "@/constants/permission"
import { CustomerContext } from "@/context/CustomerContext"
import { useNotificationContext } from "@/context/NotificationContext"
import { useSignalR } from "@/context/SignalRContext"

import { usePersonalOffersFilters } from "./PersonalOffersFilters"

import { usePersonalOfferMutation } from "@/hook/BulkOrders/usePersonalOfferMutation"
import { usePersonalOfferQuery } from "@/hook/BulkOrders/usePersonalOfferQuery"
import { usePermission } from "@/hook/usePermission"
import { useTable } from "@/hook/useTable"

import { BulkOrderStatus, bulkOrderStatuses, IBulkOrder, PERSONAL_OFFER_KEY, PersonalOfferTabs, BulkOrderStatusCase } from "@/types/IBulkOrder"
import { INotification, NotificationType } from "@/types/INotification"
import { OrderStatusTabKeys } from "@/types/IOrder"
import { TimerType } from "@/types/ITimer"
import { ITableColumn } from "@/types/ui"

import { getInitialColumns } from "@/utils/getInitialColumns"
import { isBulkOrderCustomerTimerExpired } from "@/utils/isBulkOrderTimerExpired"

const { Text } = Typography

const PersonalOffersPage: FC = () => {
    const { hasPermission } = usePermission()
    const customerContext = useContext(CustomerContext)
    const [isAdmin, setIsAdmin] = useState<boolean>(hasPermission(Resource.RequestQuotes, Scope.AllActions))
    const { notificationApi } = useNotificationContext()
    const connection = useSignalR()

    const bulkOrderComponent = useRef<BulkOrderAddFormProps>(null)

    const { changeStatusMutation, deleteMutation } = usePersonalOfferMutation()

    const currentCustomerId = customerContext.customer?.id
        ?? (Number(sessionStorage.getItem("currentCustomerId")) || undefined)

    const mainpersonalOffersColumns: ITableColumn<IBulkOrder>[] = [
      {
        title: "Информация",
        key: "id",
        dataIndex: ["id"],
        sorter: false,
        isVisible: true,
        width: 700,
        order: 1,
        render: (_, bulkOrder) =>
        <>
            <RequestQuoteInfo
                bulkOrder={bulkOrder}
            />
        </>
      },
      {
        title: "Клиент",
        key: "customerName",
        dataIndex: ["customerName"],
        isVisible: isAdmin,
        order: 2
      },
      {
        title: "Предложение действует",
        key: "createdOn",
        dataIndex: ["createdOn"],
        sorter: true,
        isVisible: true,
        order: 3,
        render: (_, { id, status, timerAdminStart, timerAdminDuration, timerCustomerStart, timerCustomerDuration })  =>
        <>
            { status === BulkOrderStatus.CurrentOffer && timerCustomerStart && timerCustomerDuration &&
                <BulkOrderTimer
                    id={id}
                    type={TimerType.BulkOrderCustomer}
                    status={status}
                    startUtc={timerCustomerStart}
                    duration={timerCustomerDuration}
                    onExpire={() => refetch()}
                />
            }
            { status === BulkOrderStatus.InProcess && timerAdminStart && timerAdminDuration &&
                <BulkOrderTimer
                    id={id}
                    type={TimerType.BulkOrderAdmin}
                    status={status}
                    startUtc={timerAdminStart}
                    duration={timerAdminDuration}
                    onExpire={() => refetch()}
                />
            }
        </>
      },
      {
          title: "Статус",
          key: "status",
          dataIndex: ["status"],
          sorter: true,
          isVisible: true,
          order: 4,
          render: (_, { status, isPersonalOffer }) =>
            <>
                <Flex justify="center">
                    <Text style={{ wordBreak: "normal", overflowWrap: "normal" }}>
                        {bulkOrderStatuses.find(x => x.type === status)?.cases[!isPersonalOffer ? BulkOrderStatusCase.Default : BulkOrderStatusCase.PersonalOffer]}
                    </Text>
                </Flex>
            </>
      },
      {
          title: "Действие",
          key: "action",
          dataIndex: ["action"],
          sorter: true,
          isVisible: true,
          order: 5,
          render: (_, { status, id, timerCustomerStart, timerCustomerDuration }) =>
          <>
              <Flex>
                  <Space>
                      {isAdmin ?
                        <>
                            <ColorifiedButton
                                themecolor="blue"
                                type={"primary"}
                                size={"small"}
                                disabled={status !== BulkOrderStatus.CurrentOffer || isBulkOrderCustomerTimerExpired(timerCustomerStart, timerCustomerDuration)}
                                onClick={() => bulkOrderComponent.current?.open(id, true)}
                            >
                                Редактировать
                            </ColorifiedButton>
                            <ColorifiedButton
                                themecolor="orange"
                                type={"primary"}
                                size={"small"}
                                disabled={status !== BulkOrderStatus.CurrentOffer || isBulkOrderCustomerTimerExpired(timerCustomerStart, timerCustomerDuration)}
                                onClick={async () => {
                                    await deleteMutation.mutateAsync({ ids: [id] })
                                    await refetch()
                                }
                                }
                            >
                                Удалить
                            </ColorifiedButton>
                        </>
                      :
                        <>
                            <ColorifiedButton
                                themecolor="green"
                                type={"primary"}
                                size={"small"}
                                disabled={
                                    status !== BulkOrderStatus.CurrentOffer
                                    || isBulkOrderCustomerTimerExpired(timerCustomerStart, timerCustomerDuration)
                                    || !currentCustomerId
                                }
                                onClick={async () => {
                                    await changeStatusMutation.mutateAsync({
                                        bulkOrderId: id,
                                        status: BulkOrderStatus.Accept,
                                        customerId: currentCustomerId
                                    })
                                    await refetch()
                                }}
                            >
                                Принять
                            </ColorifiedButton>
                            <ColorifiedButton
                                onClick={async () => {
                                    await changeStatusMutation.mutateAsync({ bulkOrderId: id, status: BulkOrderStatus.Canceled })
                                    await refetch()
                                }}
                                themecolor="orange"
                                type={"primary"}
                                size={"small"}
                                disabled={status !== BulkOrderStatus.CurrentOffer || isBulkOrderCustomerTimerExpired(timerCustomerStart, timerCustomerDuration)}
                            >
                                Отменить
                            </ColorifiedButton>
                        </>
                      }
                  </Space>
              </Flex>
          </>   
      }
    ]

    const { columns, pagination, filter, order } = useTable({
        initialColumns: getInitialColumns(mainpersonalOffersColumns, PERSONAL_OFFER_KEY),
        key: PERSONAL_OFFER_KEY
    })

    const { filters } = usePersonalOffersFilters()

    const { data, isLoading, refetch } = usePersonalOfferQuery(
        {
            Page: pagination.page,
            PageSize: pagination.pageSize,
            Filter: filter.filter,
            OrderBy: order.orderBy
        }
      )

    useEffect(() => {
        connection?.on("ReciveNotification", (notificaiton: INotification) => {
            if (notificaiton.notificationType === NotificationType.BulkOrder) {
                refetch()
            }
        })
    }, [connection, notificationApi])

    const onBulkOrderHandler = () => {
        bulkOrderComponent.current?.open(undefined, true)
    }

    return (
      <> 
        <BulkOrderAddForm ref={bulkOrderComponent}/>
        <Table
            tableTitle="Персональные предложения"
            dataSource={data?.response.data}
            columns={columns.data}
            rowKey="id"
            isLoading={isLoading}
            isSelectable={isAdmin ?  true : false}
            pagination={{
                currentPage: pagination.page,
                pageSize: pagination.pageSize,
                total: data?.response.count,
                onPageChange: pagination.onPageChange,
                onPageSizeChange: pagination.onPageSizeChange
            }}
            filters={{
              filters: filters,
              onSubmit: filter.onFilterChange,
              onCancel: filter.onFilterReset,
              filterKey: PERSONAL_OFFER_KEY
            }}
            showHeader={false}
            onSorterChange={order.setOrderBy}
            addText={isAdmin ? "Добавить" : undefined}
            onAdd={isAdmin ? onBulkOrderHandler : undefined}
            permission={{
                resource: Resource.RequestQuotes
            }}
            statusTabs={{
                defaultActiveKey: OrderStatusTabKeys.All,
                items: PersonalOfferTabs
            }}
        />
      </>
    )
}

export default PersonalOffersPage